
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import {
  required,
  minLength,
  helpers,
} from '@vuelidate/validators';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ListActionPayload, DuplicateListResult } from './types';

// Custom validator for alphanumeric with spaces and accents
const alphanumericWithSpaces = helpers.regex(/^[\w\sàâäéèêëïîôùûüÿçÀÂÄÉÈÊËÏÎÔÙÛÜŸÇ-]+$/);

export default defineComponent({
  name: 'DuplicateListAction',

  components: {
    Dialog,
    InputText,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const newName = ref('');
    const currentPayload = ref<ListActionPayload | null>(null);

    // Validation rules
    const rules = computed(() => ({
      newName: {
        required: helpers.withMessage(
          () => t('myLists.manage.actions.validation.required'),
          required,
        ),
        minLength: helpers.withMessage(
          () => t('myLists.manage.actions.validation.minLength', { min: 3 }),
          minLength(3),
        ),
        alphanumericWithSpaces: helpers.withMessage(
          () => t('myLists.manage.actions.validation.alphanumeric'),
          alphanumericWithSpaces,
        ),
      },
    }));

    const v$ = useVuelidate(rules, { newName });

    /**
     * Get the first error message for newName
     */
    const getErrorMessage = () => {
      if (v$.value.newName.$errors.length > 0) {
        return v$.value.newName.$errors[0].$message;
      }
      return '';
    };

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: ListActionPayload) => {
      currentPayload.value = payload;
      newName.value = `${t('myLists.manage.actions.cloneText')}${payload.listName}`;
      v$.value.$reset();
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      v$.value.$reset();
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentPayload.value) return;

      // Validate before confirming
      const isValid = await v$.value.$validate();
      if (!isValid) return;

      const result: DuplicateListResult = {
        listId: currentPayload.value.listId,
        newName: newName.value.trim(),
      };

      emit('confirm', result);
      close();
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      newName,
      close,
      confirm,
      v$,
      getErrorMessage,
    };
  },
});
